import type { FloorAssembly } from "building-types/floors/floorAssemblyTypes";
import type { Room } from "building-types/rooms/roomTypes";
import { RoomKind } from "building-types/rooms/roomTypesEnum";
import type { WallCladdingType, WallDescriptor } from "building-types/walls/wallDesciptorTypes";
import { WallType } from "building-types/walls/wallEnums";
import type { WallLayerType } from "building-types/walls/wallEnums";
import type { WallCondition } from "building-types/walls/wallType";
import type { WallPath, Wall, WallMass, WallLayer, WallLOD } from "building-types/walls/wallTypes";
import { getWallPath } from "building-types/walls/wallUtils";
import type { Table } from "common/data/table";
import { assertUnreachable } from "common/utils/assertUnreachable";
import uid from "common/utils/uid";
import { isInteriorRoomEnclosure } from "doc/rooms/roomAttributesTools";
import { ANGLE_EPSILON, TWO_PI } from "geom/core";
import { intersectLine2Ray2 } from "geom/intersectRay2";
import { IntersectionKind } from "geom/intersectRay2";
import { line2Eval, line2Tangent } from "geom/line2";
import { planarGraphAdjacentHalfEdge } from "geom/planarGraph";
import { polyLine2ByTwoPoints } from "geom/polyLine2";
import type { PolyLine2 } from "geom/polyLine2Types";
import { vec2AngleBetweenCW, vec2Perp, vec2Result } from "geom/vec2";
import { solidWallCrossSection } from "./solidWallCrossSection";
import { voidWallCrossSection } from "./voidWall";
import { wallLayerWithFallback } from "./wallLayers";

export { getWallPath } from "building-types/walls/wallUtils";

export function wallPathNew(
  wallType: WallType,
  wallCondition: WallCondition,
  wallDescriptor: WallDescriptor,
  halfEdge: number,
  leftRoom: Room | undefined,
  rightRoom: Room | undefined,
  leftRoomKind: RoomKind,
  rightRoomKind: RoomKind,
  startX: number,
  startY: number,
  endX: number,
  endY: number,
  length: number,
  baseOffset: number,
): WallPath {
  return {
    wallType,
    wallCondition,
    wallDescriptor,
    halfEdgeIndex: halfEdge,
    leftRoom,
    rightRoom,
    leftRoomKind,
    rightRoomKind,
    startX,
    startY,
    endX,
    endY,
    length,
    baseOffset,
  };
}

export function wallLayerNew(wallLayerType: WallLayerType, wallMass: WallMass): WallLayer {
  const newId = uid();
  const layer = {
    wallLayerType,
    ...wallMass,
    id: newId,
  };
  return layer;
}

export function wallIsBoundary(wall: Wall): boolean {
  const path = getWallPath(wall);
  return path.leftRoomKind === RoomKind.BOUNDARY || path.rightRoomKind === RoomKind.BOUNDARY;
}

export function wallsInRoomAreParallel(room: Room, wallA: Wall, wallB: Wall) {
  const angle = wallAngleBendCW(room, wallA, wallB);
  if (Math.abs(angle) < ANGLE_EPSILON || Math.abs(angle - TWO_PI) < ANGLE_EPSILON) {
    return true;
  }
  return false;
}

export function wallAngleBendCW(room: Room, wallA: Wall, wallB: Wall) {
  const [aDirX, aDirY] = wallDir(room, wallA);
  const [bDirX, bDirY] = wallDir(room, wallB);

  return vec2AngleBetweenCW(aDirX, aDirY, bDirX, bDirY);
}

export function wallNormal(room: Room, wall: Wall) {
  const path = getWallPath(wall);
  const [dirx, diry] = line2Tangent(path.startX, path.startY, path.endX, path.endY);
  if (path.leftRoom === room) {
    return vec2Perp(-dirx, -diry);
  } else {
    return vec2Perp(dirx, diry);
  }
}

function wallDir(room: Room, wall: Wall) {
  const path = getWallPath(wall);
  const [dirx, diry] = line2Tangent(path.startX, path.startY, path.endX, path.endY);
  if (path.leftRoom === room) {
    vec2Result[0] = dirx;
    vec2Result[1] = diry;
  } else {
    vec2Result[0] = -dirx;
    vec2Result[1] = -diry;
  }

  return vec2Result;
}

export function wallCenterLineStart(hostRoom: Room, wall: Wall) {
  const path = getWallPath(wall);

  if (path.leftRoom === hostRoom) {
    vec2Result[0] = path.startX;
    vec2Result[1] = path.startY;
  } else {
    vec2Result[0] = path.endX;
    vec2Result[1] = path.endY;
  }

  return vec2Result;
}

export function wallCenterLineEnd(hostRoom: Room, wall: Wall) {
  const path = getWallPath(wall);

  if (path.leftRoom === hostRoom) {
    vec2Result[0] = path.endX;
    vec2Result[1] = path.endY;
  } else {
    vec2Result[0] = path.startX;
    vec2Result[1] = path.startY;
  }

  return vec2Result;
}

export function wallCenterLineAsPolyLine2(hostRoom: Room, wall: Wall): PolyLine2 {
  const [startX, startY] = wallCenterLineStart(hostRoom, wall);
  const [endX, endY] = wallCenterLineEnd(hostRoom, wall);

  return polyLine2ByTwoPoints(startX, startY, endX, endY);
}

export function wallEvalOffset(room: Room, wall: Wall, dist: number, t: number, normalized: boolean) {
  const [normx, normy] = wallNormal(room, wall);

  const [x, y] = wallEvalCenterLine(room, wall, t, normalized);

  vec2Result[0] = x + normx * dist;
  vec2Result[1] = y + normy * dist;

  return vec2Result;
}

function wallEvalCenterLine(room: Room, wall: Wall, t: number, normalized: boolean) {
  const path = getWallPath(wall);

  if (path.leftRoom === room) {
    return line2Eval(path.startX, path.startY, path.endX, path.endY, t, normalized);
  } else {
    return line2Eval(path.endX, path.endY, path.startX, path.startY, t, normalized);
  }
}

function wallOppositeIndex(
  room: Room,
  roomWalls: Wall[],
  wallIndex: number,
  t: number = 0.5,
  normalized = true,
): number {
  // if len === 4, assume rectangular
  if (roomWalls.length === 4) {
    return (wallIndex + 2) % 4;
  }

  // fire ray normal to wall and check intersection with other room edges.
  // the closest intersected wall is the opposite wall

  const srcWall = roomWalls[wallIndex];
  const [normx, normy] = wallNormal(room, srcWall);
  const [x, y] = wallEvalCenterLine(room, srcWall, t, normalized);

  let minT = Number.POSITIVE_INFINITY;
  let minIndex = -1;

  for (let i = 0; i < roomWalls.length; i++) {
    if (i === wallIndex) {
      continue;
    }

    const wall = roomWalls[i];
    const [ax, ay] = wallCenterLineStart(room, wall);
    const [bx, by] = wallCenterLineEnd(room, wall);
    const int = intersectLine2Ray2(ax, ay, bx, by, x, y, normx, normy);

    if (!int) {
      continue;
    }

    if (int.kind === IntersectionKind.TRANSVERSE && int.t < minT) {
      minT = t;
      minIndex = i;
    }
  }

  return minIndex;
}

export function wallOpposite(
  room: Room,
  roomWalls: Wall[],
  wallIndex: number,
  t: number = 0.5,
  normalized = true,
): Wall | null {
  const i = wallOppositeIndex(room, roomWalls, wallIndex, t, normalized);
  if (i === -1) {
    return null;
  }

  return roomWalls[i];
}

export function wallWithDoorAndOppositeIndices(room: Room, walls: Wall[]) {
  const doorWallIndex = wallFirstIndexWithDoor(walls);
  if (doorWallIndex === -1) {
    // there is no entrance to the room, don't bother
    return [-1, -1];
  }

  const oppWallIndex = wallOppositeIndex(room, walls, doorWallIndex);
  if (oppWallIndex === -1) {
    return [-1, -1];
  }

  return [doorWallIndex, oppWallIndex];
}

export function wallFirstIndexWithDoor(walls: Wall[]) {
  let doorWallIndex: number = -1;

  for (let i = 0, l = walls.length; i < l; i++) {
    const wall = walls[i];
    if (wall.wallType === WallType.VOID || (wall.wallType === WallType.SOLID && wall.doorPlacements.length > 0)) {
      doorWallIndex = i;
      break;
    }
  }

  return doorWallIndex;
}

export function wallHalfEdge(room: Room, wall: Wall) {
  const path = getWallPath(wall);
  return path.leftRoom === room ? path.halfEdgeIndex : planarGraphAdjacentHalfEdge(path.halfEdgeIndex);
}

/**
 * @TODO - deprecate this. It is used in various places accross the codebase and is currently likely causing bugs
 */
export function wallThickness({
  wall,
  wallLayer,
  wallLOD,
  cutPlane,
}: {
  wall: Wall;
  wallLayer?: WallLayerType;
  wallLOD?: WallLOD;
  cutPlane?: number;
}) {
  const wallThicknessLayer = wallLayerWithFallback({
    wall,
    wallLayer,
    wallLOD,
    cutPlane,
  });
  if (wallThicknessLayer.wallType === WallType.VOID) {
    return 0;
  }
  return wallThicknessLayer.thickness;
}

/**
 * Generate a cross section of a wall for rendering, cut at a specific cutPlane.
 * This will return an empty array if no cross section exists
 *
 */
export function wallCrossSection({
  wall,
  wallLayer,
  wallLOD,
  cutPlane,
  roomFloorAssemblies,
}: {
  wall: Wall;
  wallLayer?: WallLayerType;
  wallLOD?: WallLOD;
  cutPlane?: number;
  roomFloorAssemblies?: Table<FloorAssembly>;
}) {
  switch (wall.wallType) {
    case WallType.VOID: {
      return voidWallCrossSection({
        wall,
        wallLayer,
        wallLOD,
        cutPlane,
      });
    }
    case WallType.SOLID: {
      return solidWallCrossSection({
        wall,
        wallLayer,
        wallLOD,
        cutPlane,
        roomFloorAssemblies,
      });
    }
    case WallType.SOLID_MASS: {
      return []; // TODO
    }
    default: {
      assertUnreachable(wall);
    }
  }
}

/**
 * Checks if a wall has a non-interior room on one or both sides
 */
export function wallHasNonInteriorRoomEnclosure(wall: Wall): boolean {
  const { leftRoom, rightRoom } = getWallPath(wall);

  // walls with non interior room enclosure are always considered exterior walls
  return !!(
    (leftRoom && !isInteriorRoomEnclosure[leftRoom.attributes.roomEnclosure]) ||
    (rightRoom && !isInteriorRoomEnclosure[rightRoom.attributes.roomEnclosure])
  );
}

/**
 * Check rooms on either side of a walls by a predicate
 */
export function checkWallRooms(wall: Wall, predicate: (room: Room | undefined) => boolean): boolean {
  const path = getWallPath(wall);
  return !!(predicate(path.leftRoom) || predicate(path.rightRoom));
}

/* Returns the inside and outside rooms for a wall side. */
export function getWallSideRooms(
  wall: Wall,
  isLeftSide: boolean,
): { readonly insideRoom: Room | undefined; readonly outsideRoom: Room | undefined } {
  const { leftRoom, rightRoom } = getWallPath(wall);
  return {
    insideRoom: isLeftSide ? leftRoom : rightRoom,
    outsideRoom: isLeftSide ? rightRoom : leftRoom,
  };
}

/* Returns the room whose component transform should apply to it. */
export function getHostedRoomForWall(wall: Wall, isLeftSide: boolean): Room | undefined {
  const { insideRoom, outsideRoom } = getWallSideRooms(wall, isLeftSide);
  return getHostedRoomFromPath(insideRoom, outsideRoom);
}

export function getHostedRoomFromPath(insideRoom: Room | undefined, outsideRoom: Room | undefined): Room | undefined {
  if (insideRoom?.componentTransform) {
    return insideRoom;
  } else if (outsideRoom?.componentTransform) {
    return outsideRoom;
  }
  return undefined;
}

export function wallSideHasCladdingType(wall: Wall, claddinngType: WallCladdingType, isLeftSide: boolean): boolean {
  const mass = wall.wallType === WallType.SOLID ? wall.mass : wall;

  const sectionSide = isLeftSide ? mass.section.leftSide : mass.section.rightSide;
  let sideHasCladdingType = sectionSide.cladding === claddinngType;
  if (!sideHasCladdingType) {
    sideHasCladdingType = !!mass.layers.find((l) => wallSideHasCladdingType(l, claddinngType, isLeftSide));
  }

  return sideHasCladdingType;
}
